import React, { useEffect, useRef, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Tooltip } from '@mui/material';
import { tipSlotProps } from './tip-font';

function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(pointer: coarse)');
    const apply = () => setCoarse(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  return coarse;
}

function FieldTip({ title }: { title: string }): JSX.Element {
  const coarse = useCoarsePointer();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && anchorRef.current && anchorRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutside, true);
    return () => document.removeEventListener('pointerdown', closeOnOutside, true);
  }, [open]);

  return (
    <Tooltip
      title={title}
      describeChild
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableHoverListener={coarse}
      disableFocusListener
      disableTouchListener
      slotProps={tipSlotProps}
    >
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        aria-label="Help"
        onClick={() => {
          if (!coarse) return;
          setOpen((current) => !current);
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          ml: 0.5,
          p: '6px',
          border: 0,
          background: 'none',
          color: 'var(--qc-shell-muted, #9fb0c8)',
          cursor: 'pointer',
          fontSize: 'inherit',
          lineHeight: 0,
          verticalAlign: 'middle',
        }}
      >
        <InfoOutlinedIcon aria-hidden fontSize="inherit" />
      </Box>
    </Tooltip>
  );
}

export default FieldTip;
